/**
 * M03 方案管理 shell · PB 列表 / PB 编辑 / RoutingTable / 兜底映射
 *
 * 承接：T11 §3.2 路由 /playbook = M03 · M03 README 子页规划
 */
import { useState } from "react";

import { IntentMappingMatrix } from "@/components/m03/intent-mapping-matrix";
import { PlaybookEditor } from "@/components/m03/playbook-editor";
import { PlaybookList } from "@/components/m03/playbook-list";
import { RoutingTablePanel } from "@/components/m03/routing-table-panel";
import { type RuleSurfaceRow, RuleSurfaceSummary } from "@/components/shared/rule-surface-summary";
import {
	SingleStageTestPanel,
	type SingleStageTestSample,
} from "@/components/shared/single-stage-test-panel";

type Tab = "list" | "editor" | "routing" | "mapping";

const TABS: ReadonlyArray<{ id: Tab; label: string; testid: string }> = [
	{ id: "list", label: "方案列表", testid: "m03-tab-list" },
	{ id: "editor", label: "方案编辑", testid: "m03-tab-editor" },
	{ id: "routing", label: "路由规则", testid: "m03-tab-routing" },
	{ id: "mapping", label: "兜底映射", testid: "m03-tab-mapping" },
];

const PLAYBOOK_TEST_SAMPLES: SingleStageTestSample[] = [
	{
		sampleId: "P10TA001",
		sampleMessage: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPlaybook: "PB02_refund",
		scenario: "PB02 退款办理 / 缺订单信息",
		verificationPoint: "能命中退款方案，并提示后续要补订单信息。",
		assetName: "分类到方案映射 / PB02 退款办理",
		boundary: "只验证处理路径匹配，不执行退款。",
	},
	{
		sampleId: "P10TA003",
		sampleMessage: "你们店里有什么滑雪板？",
		expectedIntent: "product_inquiry",
		expectedPlaybook: "PB06_pre_sale",
		scenario: "PB06 售前导购 / 商品事实",
		verificationPoint: "商品泛问进入售前导购，后续由商品事实和知识证据支撑。",
		assetName: "分类到方案映射 / PB06 售前导购",
		boundary: "只验证方案命中，不判断商品事实是否完整。",
	},
	{
		sampleId: "P10TA004",
		sampleMessage: "新手滑雪板和礼品卡我应该选哪个？",
		expectedIntent: "product_recommendation",
		expectedPlaybook: "PB06_pre_sale",
		scenario: "PB06 售前导购 / 推荐对比",
		verificationPoint: "推荐、对比仍由 PB06 统一承接，避免把导购拆乱。",
		assetName: "分类到方案映射 / PB06 售前导购",
		boundary: "只验证 PB06 召回，不在这里生成最终推荐话术。",
	},
	{
		sampleId: "P11TA011",
		sampleMessage: "我收到的板子坏了，怎么处理？",
		expectedIntent: "order_issue",
		expectedPlaybook: "PB03_return",
		scenario: "PB03 收货问题 / RoutingTable",
		verificationPoint: "订单异常先由 RoutingTable 路由到售后处理方案，branch 细分后续再做。",
		assetName: "RoutingTable / PB03 退货办理",
		boundary: "只验证处理路径，不创建退货或退款。",
	},
	{
		sampleId: "P10TA006",
		sampleMessage: "这个优惠码 SPRING10 还能用吗？",
		expectedIntent: "discount_inquiry",
		expectedPlaybook: "PB07_discount",
		scenario: "PB07 优惠咨询 / 规则查询",
		verificationPoint: "优惠码问题命中 PB07，后续由 SK06 或知识补事实。",
		assetName: "分类到方案映射 / PB07 优惠咨询",
		boundary: "只验证处理路径匹配，不承诺真实优惠码可用。",
	},
	{
		sampleId: "P10TA007",
		sampleMessage: "这单还没发货可以取消吗？",
		expectedIntent: "order_cancel",
		expectedPlaybook: "PB04_cancel",
		scenario: "PB04 取消订单 / 写操作保护",
		verificationPoint: "取消诉求命中受控动作方案，后续必须确认和 dry-run。",
		assetName: "分类到方案映射 / PB04 取消订单",
		boundary: "只验证处理路径匹配，不执行取消。",
	},
	{
		sampleId: "P10TA009",
		sampleMessage: "我地址写错了，能改到新地址吗？",
		expectedIntent: "address_change",
		expectedPlaybook: "PB05_address_change",
		scenario: "PB05 地址修改 / 状态约束",
		verificationPoint: "地址修改命中 PB05，后续根据发货状态决定能否执行。",
		assetName: "分类到方案映射 / PB05 地址修改",
		boundary: "只验证处理路径匹配，不执行地址修改。",
	},
];

const PLAYBOOK_RULE_ROWS: RuleSurfaceRow[] = [
	{
		name: "方案列表",
		source: "7 个 Playbook YAML 摘要",
		edit: "列表页只查看和选择",
		effect: "只读说明",
		verify: "确认当前方案覆盖哪些意图和技能",
		tone: "readonly",
	},
	{
		name: "方案 YAML",
		source: "Playbook YAML：步骤、PB/分支允许技能、预取条件、兜底话术",
		edit: "可编辑，保存时校验 schema 和版本",
		effect: "主链路生效",
		verify: "保存后跑方案测试，再跑链路测试台看证据和回复",
		tone: "live",
	},
	{
		name: "路由规则",
		source: "routing_table.yaml：intent + sub_task + 条件 + branch",
		edit: "可编辑 YAML，并有矩阵预览",
		effect: "主链路优先生效",
		verify: "确认 order_issue 进入 PB03 对应分支，状态查询 short_circuit 进入 SK02",
		tone: "live",
	},
	{
		name: "兜底映射",
		source: "intent_playbook_mapping.yaml（RoutingTable 未命中时使用）",
		edit: "可编辑 YAML，并有矩阵预览",
		effect: "主链路生效",
		verify: "确认普通 intent 仍能回退到既有 PB",
		tone: "live",
	},
];

export type M03View = "management" | "test";

export function M03Shell({ view = "management" }: { view?: M03View }) {
	const [tab, setTab] = useState<Tab>("list");
	const [selectedPbId, setSelectedPbId] = useState<string | null>(null);

	const handleSelect = (pbId: string) => {
		setSelectedPbId(pbId);
		setTab("editor");
	};

	if (view === "test") {
		return (
			<SingleStageTestPanel
				testId="playbook-single-stage-test-panel"
				kind="playbook"
				title="处理方案单环节测试"
				description="用同一条问题验证意图是否能命中正确处理方案，并检查缺参追问、兜底和评测运营承接是否清楚。"
				targetStage="playbook_routing"
				stageLabel="处理方案"
				sampleId="P10TA001"
				sampleMessage="我要退款，但是忘记订单号了"
				expectedIntent="refund_request"
				expectedPlaybook="PB02_refund"
				assetName="RoutingTable / 兜底映射 / 方案步骤"
				boundary="当前验证处理路径匹配结果，不代表工具事实或最终回复已全部通过。"
				datasetHref="/test-datasets?mock=1#p10-single-stage-full-menu-coverage"
				samples={PLAYBOOK_TEST_SAMPLES}
			/>
		);
	}

	return (
		<div className="space-y-4" data-testid="m03-shell">
			<RuleSurfaceSummary
				rows={PLAYBOOK_RULE_ROWS}
				testId="playbook-rule-surface-summary"
				description="这个页面管“意图命中后走哪套处理路径”。RoutingTable 优先命中方案或短路径，旧 mapping 只在未命中时兜底。"
			/>

			<nav className="flex gap-1 border-b border-surface-border">
				{TABS.map((t) => (
					<button
						type="button"
						key={t.id}
						data-testid={t.testid}
						className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
							tab === t.id
								? "border-brand-accent font-medium text-brand-accent"
								: "border-transparent text-text-secondary hover:text-text-primary"
						}`}
						onClick={() => setTab(t.id)}
					>
						{t.label}
					</button>
				))}
			</nav>

			<section className="pt-2">
				{tab === "list" ? (
					<PlaybookList onSelect={handleSelect} selectedPbId={selectedPbId} />
				) : null}
				{tab === "editor" ? (
					<PlaybookEditor selectedPbId={selectedPbId} onSelectPbId={setSelectedPbId} />
				) : null}
				{tab === "routing" ? <RoutingTablePanel /> : null}
				{tab === "mapping" ? <IntentMappingMatrix /> : null}
			</section>
		</div>
	);
}
